import React from 'react';

import { CloseOutlined, SearchOutlined, UpOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import FilterActiveTags from '@src/components/FilterActiveTags/FilterActiveTags';
import ListViewportLayout from '@src/components/ListViewportLayout';
import SecondaryPanel from '@src/components/SecondaryPanel/SecondaryPanel';
import { FilterValues } from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import IntlMessages from '@src/util/IntlMessages';

import { Alert, Drawer, Input, Spin } from 'antd';
import classNames from 'classnames';

import ActiveUserDashboardFilters from './components/ActiveUserDashboardFilters';
import ActiveUserComplianceDrawer from './components/ActiveUserComplianceDrawer';
import ActiveUserKpis from './components/ActiveUserKpis';
import ActiveUserList from './components/ActiveUserList';
import ActiveUserMap from './components/ActiveUserMap';
import ActiveUserDashboardRoutes from './ActiveUserDashboardRoutes';
import { useActiveUserDashboard } from './hooks/useActiveUserDashboard';
import { useNowTicker } from './hooks/useNowTicker';
import { VIEWPORT_OFFSET } from './constants';
import { getConfiguredTimeZone } from './utils';
import styles from './styles.module.less';

const UserInfo = React.lazy(() => import('@src/packages/users/screens/UserInfo'));

const ActiveUserDashboard: React.FC = () => {
  const intl = useIntl();
  const {
    url,
    orgId,
    listCollapsed,
    focusRequestId,
    fitAllRequestId,
    listLoading,
    fitAllReady,
    toggleListCollapsed,
    profileUserId,
    openUserProfile,
    canViewUserProfile,
    closeUserProfile,
    isMobile,
    error,
    dataStreamError,
    userFocused,
    recordsToShow,
    mobileListUsers,
    showMobileSearch,
    mobileUserSearch,
    handleMobileUserSearch,
    listContainerRef,
    listOverlayRef,
    listPagination,
    kpis,
    sortState,
    setSortState,
    mapRef,
    userTrackerRef,
    filters,
    setFilters,
    initialFilters,
    toggle,
    isOpen,
    mobileListExpanded,
    toggleMobileList,
    handleSheetPointerDown,
    handleSheetPointerMove,
    handleSheetPointerUp,
    handleSheetPointerCancel,
    handleFindUser,
    handleTimelineByUser,
    handleSchedulesByUser,
    handleMarkerClick,
    handleMarkerClose,
    handleKpiFilter,
    complianceDrawer,
    complianceDetails,
    closeComplianceDrawer,
    openComplianceDrawer,
  } = useActiveUserDashboard();

  const nowMs = useNowTicker();
  const timeZone = React.useMemo(() => getConfiguredTimeZone(), []);
  const initialFiltersRef = React.useRef(initialFilters);

  const filterPanel = React.useMemo(
    () => <ActiveUserDashboardFilters handleFilters={setFilters} filters={filters} />,
    [filters, setFilters],
  );

  const actionButtons = React.useMemo(() => {
    const buttons = [
      {
        label: intl.formatMessage({
          id: isOpen ? 'filterDropdown.closeFilters' : 'dashboard.tooltip.filter',
        }),
        iconName: 'filter_alt',
        type: 'secondary' as const,
        action: toggle,
        className: isOpen ? 'gx-border-primary' : '',
      },
    ];

    return buttons;
  }, [intl, isOpen, toggle]);

  const listHidden = isMobile ? !mobileListExpanded : listCollapsed;

  return (
    <>
      <SecondaryPanel.Panel
        title={intl.formatMessage({ id: 'dashboards.chart.filters' })}
        initialValues={initialFiltersRef.current as FilterValues}
      >
        {filterPanel}
      </SecondaryPanel.Panel>
      <BoxContainer fullContent fixed bottomBorder contentClassName={isMobile ? styles.mobileHeaderContent : undefined}>
        <FilterContainer
          compactHeader={isMobile}
          title={<Title.Header value={<IntlMessages id="dashboards.active_users" />} />}
          actionButtons={actionButtons}
        />
      </BoxContainer>
      <FilterActiveTags.Container>
        <FilterActiveTags.Bar>
          <FilterActiveTags.ScrollArea>
            <FilterActiveTags onAddFilter={toggle} />
          </FilterActiveTags.ScrollArea>
        </FilterActiveTags.Bar>
      </FilterActiveTags.Container>
      <BoxContainer
        fullContent
        error={error}
        className={classNames('gx-w-100', styles.mapBox)}
        contentClassName={styles.fullBleed}
      >
        <SecondaryPanel.Content>
          <ListViewportLayout
            viewportOffset={VIEWPORT_OFFSET}
            sidePanelOpen={isOpen}
            scrollable={false}
            fillViewport
            bodyClassName={styles.viewportBody}
            className={styles.viewport}
          >
            <div ref={listContainerRef} className={styles.activerUserContent}>
              <div className={styles.mapHud}>
                {dataStreamError ? (
                  <Alert
                    className="gx-mb-2"
                    type="warning"
                    showIcon
                    message={<IntlMessages id="activeUserDashboard.liveData.warning" />}
                  />
                ) : null}
              </div>
              <div ref={listOverlayRef} className={classNames(
                isMobile ? styles.usersSheet : styles.usersList,
                isMobile && !mobileListExpanded && styles.usersSheetCollapsed,
                !isMobile && !listCollapsed && listPagination.total > listPagination.pageSize && styles.usersListPaginated,
                !isMobile && listCollapsed && styles.usersListCollapsed,
              )}>
                {!isMobile && <div className={styles.listHeader}>
                  <strong className={styles.listHeaderTitle}><IntlMessages id="activeUserDashboard.list.title" /></strong>
                  {!listCollapsed && (
                    <div className={styles.listHeaderKpis}>
                      <ActiveUserKpis kpis={kpis} onKpiClick={handleKpiFilter} />
                    </div>
                  )}
                  <button
                    type="button"
                    className={styles.listToggleButton}
                    onClick={toggleListCollapsed}
                    aria-expanded={!listHidden}
                    aria-controls="active-users-table"
                    aria-label={intl.formatMessage({ id: listHidden ? 'activeUserDashboard.list.expand' : 'activeUserDashboard.list.collapse' })}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">dock_to_right</span>
                  </button>
                </div>}
                {isMobile && <button
                  type="button"
                  className={styles.sheetToggle}
                  onClick={toggleMobileList}
                  onPointerDown={handleSheetPointerDown}
                  onPointerMove={handleSheetPointerMove}
                  onPointerUp={handleSheetPointerUp}
                  onPointerCancel={handleSheetPointerCancel}
                  aria-expanded={!listHidden}
                  aria-controls="active-users-table"
                  aria-label={intl.formatMessage({
                    id: listHidden ? 'activeUserDashboard.sheet.expand' : 'activeUserDashboard.sheet.collapse',
                  })}
                >
                  <span className={styles.sheetHandle} aria-hidden="true" />
                  <span className={styles.sheetHeading}>
                    <strong><IntlMessages id="activeUserDashboard.list.title" /></strong>
                    <span><IntlMessages id="activeUserDashboard.results.count" values={{ count: mobileListUsers.length }} /></span>
                  </span>
                  {mobileListExpanded ? <CloseOutlined aria-hidden="true" /> : <UpOutlined aria-hidden="true" />}
                </button>}
                <div
                  id="active-users-table"
                  data-testid="active-users-table"
                  className={isMobile ? styles.sheetBody : classNames(styles.tableBody, listCollapsed && styles.tableBodyCollapsed)}
                  aria-hidden={listHidden}
                >
                  {isMobile && mobileListExpanded && showMobileSearch && <div className={styles.sheetTools}>
                    <Input
                      className={styles.sheetSearch}
                      prefix={<SearchOutlined aria-hidden="true" />}
                      allowClear
                      value={mobileUserSearch}
                      onChange={handleMobileUserSearch}
                      placeholder={intl.formatMessage({ id: 'users.selectionDrawer.searchPlaceholder' })}
                      aria-label={intl.formatMessage({ id: 'users.selectionDrawer.searchPlaceholder' })}
                    />
                  </div>}
                  {(!isMobile || mobileListExpanded) && <ActiveUserList
                    users={isMobile ? mobileListUsers : recordsToShow}
                    loading={listLoading}
                    pagination={listPagination}
                    isMobile={isMobile}
                    nowMs={nowMs}
                    timeZone={timeZone}
                    sortState={sortState}
                    onSortChange={setSortState}
                    onFindUser={handleFindUser}
                    onTimeline={handleTimelineByUser}
                    onSchedules={handleSchedulesByUser}
                    onOpenCompliance={openComplianceDrawer}
                  />}
                </div>
              </div>
              <div className={classNames(
                styles['map-container'], styles.visible,
                isMobile && styles.mobileMap,
              )}>
                <ActiveUserMap
                  overlayRef={listOverlayRef}
                  isMobile={isMobile}
                  ref={userTrackerRef}
                  mapRef={mapRef}
                  users={recordsToShow}
                  currentUser={userFocused}
                  focusRequestId={focusRequestId}
                  fitAllRequestId={fitAllRequestId}
                  fitAllReady={fitAllReady}
                  onMarkerClick={handleMarkerClick}
                  onMarkerClose={handleMarkerClose}
                  onUserInfo={canViewUserProfile ? openUserProfile : undefined}
                />
              </div>
            </div>
          </ListViewportLayout>
        </SecondaryPanel.Content>
      </BoxContainer>

      <ActiveUserComplianceDrawer
        open={complianceDrawer !== null}
        userName={complianceDrawer?.user.displayName ?? ''}
        type={complianceDrawer?.type ?? null}
        executions={complianceDetails}
        isMobile={isMobile}
        onClose={closeComplianceDrawer}
      />

      <Drawer
        className={styles.profileDrawer}
        open={canViewUserProfile && profileUserId !== null}
        onClose={closeUserProfile}
        width={isMobile ? '100%' : 960}
        destroyOnClose
        closable={false}
        title={null}
        footer={null}
      >
        <BoxContainer content fixed bottomBorder className={styles.profileDrawerHeader}>
          <FilterContainer
            goBack={closeUserProfile}
            title={(
              <Title.Header
                value={recordsToShow.find(user => user.id === profileUserId)?.displayName ??
                  intl.formatMessage({ id: 'user.profile.title' })}
              />
            )}
          />
        </BoxContainer>
        {canViewUserProfile && profileUserId && (
          <React.Suspense fallback={<Spin />}>
            <UserInfo userId={profileUserId} orgId={orgId} showBack={false} showHeader={false} embedded />
          </React.Suspense>
        )}
      </Drawer>
      <ActiveUserDashboardRoutes url={url} />
    </>
  );
};

export default ActiveUserDashboard;
